import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../contexts/ToastContext";
import {
  LoginFormData,
  LoginFormErrors,
  SignUpFormData,
  SignUpFormErrors,
  ForgotPasswordFormData,
  ForgotPasswordFormErrors,
} from "../types";
import "../styles/Login.css";
import { loginUser, registerUser, validateUserEmail } from "../api/userApi";
import { UserModel } from "../types/user";

type ViewMode = "signin" | "signup" | "forgot-password";

const Login: React.FC = () => {
  const { signUp, isLoading, error, clearError } = useAuth();
  const { showToast } = useToast();
  const [viewMode, setViewMode] = useState<ViewMode>("signin");

  // Sign In Form
  const [loginFormData, setLoginFormData] = useState<LoginFormData>({
    username: "",
    password: "",
    rememberMe: false,
  });
  const [loginErrors, setLoginErrors] = useState<LoginFormErrors>({});

  // Sign Up Form
  const [signUpFormData, setSignUpFormData] = useState<SignUpFormData>({
    email: "",
    password: "",
    confirmPassword: "",
    name: "",
    mobileNumber: "",
  });
  const [signUpErrors, setSignUpErrors] = useState<SignUpFormErrors>({});
  const [userData, setUserData] = useState<UserModel | null>(null);
  // Forgot Password Form
  const [forgotPasswordData, setForgotPasswordData] =
    useState<ForgotPasswordFormData>({
      email: "",
    });
  const [forgotPasswordErrors, setForgotPasswordErrors] =
    useState<ForgotPasswordFormErrors>({});

  // Clear errors when switching views
  useEffect(() => {
    clearError();
    setLoginErrors({});
    setSignUpErrors({});
    setForgotPasswordErrors({});
  }, [viewMode]);

  // Clear errors when user starts typing
  useEffect(() => {
    if (error) {
      clearError();
    }
  }, [loginFormData.username, loginFormData.password]);

  const handleLoginInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setLoginFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    if (loginErrors[name as keyof LoginFormErrors]) {
      setLoginErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const handleSignUpInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setSignUpFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (signUpErrors[name as keyof SignUpFormErrors]) {
      setSignUpErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const handleForgotPasswordInputChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = e.target;
    setForgotPasswordData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (forgotPasswordErrors[name as keyof ForgotPasswordFormErrors]) {
      setForgotPasswordErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const validateLoginForm = (): boolean => {
    const newErrors: LoginFormErrors = {};

    if (!loginFormData.username.trim()) {
      newErrors.username = "Username is required";
    } else if (loginFormData.username.trim().length < 3) {
      newErrors.username = "Username must be at least 3 characters";
    }

    if (!loginFormData.password.trim()) {
      newErrors.password = "Password is required";
    } else if (loginFormData.password.trim().length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }

    setLoginErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateSignUpForm = async (): Promise<boolean> => {
    const newErrors: SignUpFormErrors = {};

    // EMAIL BASIC VALIDATION
    if (!signUpFormData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(signUpFormData.email)) {
      newErrors.email = "Please enter a valid email address";
    } else {
      // ✅ Call API ONLY if email is valid
      const emailValidatorResponse = await validateUserEmail(
        signUpFormData.email.trim()
      );

      const emailValidator = emailValidatorResponse.data;

      if (!emailValidator.isValidEmail) {
        newErrors.email = "Email Address not registered";
      } else if (emailValidator.isAlreadyUser) {
        newErrors.email = "Email is already associated with an account";
      }
    }

    // PASSWORD
    if (!signUpFormData.password.trim()) {
      newErrors.password = "Password is required";
    } else if (signUpFormData.password.trim().length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }

    // CONFIRM PASSWORD
    if (!signUpFormData.confirmPassword.trim()) {
      newErrors.confirmPassword = "Please confirm your password";
    } else if (signUpFormData.password !== signUpFormData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    // MOBILE
    if (!signUpFormData.mobileNumber.trim()) {
      newErrors.mobileNumber = "Mobile number is required";
    } else if (!/^[6-9]\d{9}$/.test(signUpFormData.mobileNumber)) {
      newErrors.mobileNumber = "Please enter a valid 10-digit mobile number";
    }

    setSignUpErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateForgotPasswordForm = (): boolean => {
    const newErrors: ForgotPasswordFormErrors = {};

    if (!forgotPasswordData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(forgotPasswordData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    setForgotPasswordErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleLoginSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (validateLoginForm()) {
      const response = await loginUser({
        email: loginFormData.username.trim(),
        password: loginFormData.password.trim(),
        mobileNumber: "", // Not needed for login
      });

      const result = response.data;

      if (result === "Wrong Credentials") {
        setLoginErrors({
          general: "Login failed. Please check your credentials.",
        });
      } else {
        // Login successful - store authentication data
        const token = `token_${Date.now()}`;
        const userData = {
          id: Date.now().toString(),
          username: loginFormData.username.trim(),
          email: loginFormData.username.trim(),
          role: "student" as const,
          name: loginFormData.username.trim(),
          avatar: loginFormData.username.charAt(0).toUpperCase(),
          studentId: "STU" + Date.now().toString().slice(-6),
          mobileNumber: "",
          department: "Computer Science",
        };

        // Store in localStorage to trigger authentication
        localStorage.setItem("auth-token", token);
        localStorage.setItem("user-data", JSON.stringify(userData));

        // Show success message and reload to trigger auth check
        showToast("Login successful! Welcome back.", "success");

        // Force auth context to re-check authentication
        window.location.reload();
      }
    }
  };

  const handleSignUpSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const validate = await validateSignUpForm();
    if (validate) {
      console.log("Sign Up Form Data:", signUpFormData);
      const userData: UserModel = {
        email: signUpFormData.email.trim(),
        password: signUpFormData.password.trim(),
        mobileNumber: signUpFormData.mobileNumber.trim(),
      };
      const success = (await registerUser(userData)).data;

      if (success != "User Inserted Successfully") {
        setSignUpErrors({
          general: error || "Sign up failed. Please try again.",
        });
      } else {
        showToast("Registration successful! Please sign in.", "success");
        setViewMode("signin");
      }
    }
  };

  const handleForgotPasswordSubmit = async (
    e: React.FormEvent<HTMLFormElement>
  ) => {
    e.preventDefault();

    if (validateForgotPasswordForm()) {
      // Simulate password reset
      await new Promise((resolve) => setTimeout(resolve, 1000));
      showToast(
        "Password reset link has been sent to your email address.",
        "success"
      );
      setViewMode("signin");
    }
  };

  return (
    <div className="login-container">
      <div className="login-left-panel">
        <div className="login-branding">
          <div className="logo-container">
            <div className="logo-icon">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M8 7h8"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M8 11h8"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M8 15h4"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <div className="logo-text">
              <h1>Library Management</h1>
              <span>Student Portal</span>
            </div>
          </div>
          <div className="branding-content">
            <h2>Welcome to Student Portal</h2>
            <p>
              Access your student profile, manage your seat, view payment
              history, and more.
            </p>
            <div className="features-list">
              <div className="feature-item">
                <div className="feature-icon">👤</div>
                <span>Student Profile</span>
              </div>
              <div className="feature-item">
                <div className="feature-icon">🪑</div>
                <span>Seat Management</span>
              </div>
              <div className="feature-item">
                <div className="feature-icon">💳</div>
                <span>Payment History</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="login-right-panel">
        <div className="login-card">
          <div className="login-header">
            <h3>
              {viewMode === "signin" && "Sign In"}
              {viewMode === "signup" && "Create New Account"}
              {viewMode === "forgot-password" && "Forgot Password"}
            </h3>
            <p>
              {viewMode === "signin" &&
                "Enter your credentials to access your account"}
              {viewMode === "signup" &&
                "Fill in your details to create a new account"}
              {viewMode === "forgot-password" &&
                "Enter your email to reset your password"}
            </p>
          </div>

          {/* Sign In Form */}
          {viewMode === "signin" && (
            <form onSubmit={handleLoginSubmit} className="login-form">
              {error && (
                <div className="error-banner">
                  <span className="error-icon">⚠️</span>
                  {error}
                </div>
              )}

              {loginErrors.general && (
                <div className="error-banner">
                  <span className="error-icon">⚠️</span>
                  {loginErrors.general}
                </div>
              )}

              <div className="form-group">
                <label htmlFor="username" className="form-label">
                  Username
                </label>
                <input
                  type="text"
                  id="username"
                  name="username"
                  value={loginFormData.username}
                  onChange={handleLoginInputChange}
                  className={`form-input ${
                    loginErrors.username ? "error" : ""
                  }`}
                  placeholder="Enter your username"
                  disabled={isLoading}
                  autoComplete="username"
                />
                {loginErrors.username && (
                  <span className="error-message">{loginErrors.username}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="password" className="form-label">
                  Password
                </label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={loginFormData.password}
                  onChange={handleLoginInputChange}
                  className={`form-input ${
                    loginErrors.password ? "error" : ""
                  }`}
                  placeholder="Enter your password"
                  disabled={isLoading}
                  autoComplete="current-password"
                />
                {loginErrors.password && (
                  <span className="error-message">{loginErrors.password}</span>
                )}
              </div>

              <div className="form-group checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="rememberMe"
                    checked={loginFormData.rememberMe}
                    onChange={handleLoginInputChange}
                    disabled={isLoading}
                    className="checkbox-input"
                  />
                  <span className="checkbox-custom"></span>
                  Remember me
                </label>
              </div>

              <button
                type="submit"
                className={`login-button ${isLoading ? "loading" : ""}`}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <span className="spinner"></span>
                    Signing in...
                  </>
                ) : (
                  "Sign In"
                )}
              </button>

              <div className="form-footer">
                <button
                  type="button"
                  className="link-button"
                  onClick={() => setViewMode("forgot-password")}
                >
                  Forgot Password?
                </button>
                <div className="divider-text">or</div>
                <button
                  type="button"
                  className="link-button primary"
                  onClick={() => setViewMode("signup")}
                >
                  Create New Account
                </button>
              </div>
            </form>
          )}

          {/* Sign Up Form */}
          {viewMode === "signup" && (
            <form onSubmit={handleSignUpSubmit} className="login-form">
              {signUpErrors.general && (
                <div className="error-banner">
                  <span className="error-icon">⚠️</span>
                  {signUpErrors.general}
                </div>
              )}

              <div className="form-group">
                <label htmlFor="signup-email" className="form-label">
                  Email
                </label>
                <input
                  type="email"
                  id="signup-email"
                  name="email"
                  value={signUpFormData.email}
                  onChange={handleSignUpInputChange}
                  className={`form-input ${signUpErrors.email ? "error" : ""}`}
                  placeholder="Enter your email"
                  disabled={isLoading}
                />
                {signUpErrors.email && (
                  <span className="error-message">{signUpErrors.email}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="signup-mobile" className="form-label">
                  Mobile Number
                </label>
                <input
                  type="tel"
                  id="signup-mobile"
                  name="mobileNumber"
                  value={signUpFormData.mobileNumber}
                  onChange={handleSignUpInputChange}
                  className={`form-input ${
                    signUpErrors.mobileNumber ? "error" : ""
                  }`}
                  placeholder="Enter your mobile number"
                  disabled={isLoading}
                />
                {signUpErrors.mobileNumber && (
                  <span className="error-message">
                    {signUpErrors.mobileNumber}
                  </span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="signup-password" className="form-label">
                  Password
                </label>
                <input
                  type="password"
                  id="signup-password"
                  name="password"
                  value={signUpFormData.password}
                  onChange={handleSignUpInputChange}
                  className={`form-input ${
                    signUpErrors.password ? "error" : ""
                  }`}
                  placeholder="Create a password"
                  disabled={isLoading}
                />
                {signUpErrors.password && (
                  <span className="error-message">{signUpErrors.password}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="signup-confirm-password" className="form-label">
                  Confirm Password
                </label>
                <input
                  type="password"
                  id="signup-confirm-password"
                  name="confirmPassword"
                  value={signUpFormData.confirmPassword}
                  onChange={handleSignUpInputChange}
                  className={`form-input ${
                    signUpErrors.confirmPassword ? "error" : ""
                  }`}
                  placeholder="Confirm your password"
                  disabled={isLoading}
                />
                {signUpErrors.confirmPassword && (
                  <span className="error-message">
                    {signUpErrors.confirmPassword}
                  </span>
                )}
              </div>

              <button
                type="submit"
                className={`login-button ${isLoading ? "loading" : ""}`}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <span className="spinner"></span>
                    Creating account...
                  </>
                ) : (
                  "Create Account"
                )}
              </button>

              <div className="form-footer">
                <button
                  type="button"
                  className="link-button"
                  onClick={() => setViewMode("signin")}
                >
                  Already have an account? Sign In
                </button>
              </div>
            </form>
          )}

          {/* Forgot Password Form */}
          {viewMode === "forgot-password" && (
            <form onSubmit={handleForgotPasswordSubmit} className="login-form">
              {forgotPasswordErrors.general && (
                <div className="error-banner">
                  <span className="error-icon">⚠️</span>
                  {forgotPasswordErrors.general}
                </div>
              )}

              <div className="form-group">
                <label htmlFor="forgot-email" className="form-label">
                  Email Address
                </label>
                <input
                  type="email"
                  id="forgot-email"
                  name="email"
                  value={forgotPasswordData.email}
                  onChange={handleForgotPasswordInputChange}
                  className={`form-input ${
                    forgotPasswordErrors.email ? "error" : ""
                  }`}
                  placeholder="Enter your email address"
                  disabled={isLoading}
                />
                {forgotPasswordErrors.email && (
                  <span className="error-message">
                    {forgotPasswordErrors.email}
                  </span>
                )}
              </div>

              <button
                type="submit"
                className={`login-button ${isLoading ? "loading" : ""}`}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <span className="spinner"></span>
                    Sending...
                  </>
                ) : (
                  "Send Reset Link"
                )}
              </button>

              <div className="form-footer">
                <button
                  type="button"
                  className="link-button"
                  onClick={() => setViewMode("signin")}
                >
                  Back to Sign In
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;
